import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { LayoutGrid, List } from "lucide-react";
import { FadeIn } from "@/components/common/FadeIn";
import { SEO } from "@/components/common/SEO";
import api from "@/lib/api";

const MAX_WAIT = 500;

/**
 * Pick the image for an artist card, by priority:
 *   1. The piece explicitly flagged `artist_card_image: true` by the admin
 *   2. The artist's portrait photo
 * Match is by artist_id OR (case-insensitive) artist_name.
 */
function pickArtistCardImage(artist, artworks) {
  if (!artist) return null;
  const artistName = (artist.name || "").trim().toLowerCase();
  const mine = artworks.filter(
    (w) =>
      (artist.id && w.artist_id === artist.id) ||
      (artistName && (w.artist_name || "").trim().toLowerCase() === artistName)
  );

  const cardPick = mine.find((w) => w.artist_card_image && w.image);
  if (cardPick) return cardPick.image;

  return artist.photo || null;
}

export default function Artists() {
  const [artists, setArtists] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [view, setView] = useState("grid");
  const [loaded, setLoaded] = useState(false);
  const [animateReady, setAnimateReady] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/artists"), api.get("/artworks")]).then(([a, w]) => {
      setArtists(a.data);
      setArtworks(w.data);
      setLoaded(true);
    });
  }, []);

  // Memoize per-artist image selection so random picks are stable across renders
  // (re-rolls only when the artists/artworks lists themselves change).
  const artistImages = useMemo(() => {
    const map = {};
    for (const artist of artists) {
      map[artist.id] = pickArtistCardImage(artist, artworks);
    }
    return map;
  }, [artists, artworks]);

  // Phase 2: wait for first artist image or max timeout
  useEffect(() => {
    if (!loaded || artists.length === 0) {
      if (loaded) setAnimateReady(true);
      return;
    }

    let resolved = false;
    const resolve = () => {
      if (resolved) return;
      resolved = true;
      setAnimateReady(true);
    };

    const timer = setTimeout(resolve, MAX_WAIT);

    const firstImg = artists[0] && artistImages[artists[0].id];
    if (firstImg) {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.src = firstImg;
    } else {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    }

    return () => clearTimeout(timer);
  }, [loaded, artists, artworks, artistImages]);

  const getArtistImage = (artist) => artistImages[artist.id] || artist.photo;

  return (
    <div data-testid="artists-page" className="pt-20 bg-[#fffefd] min-h-screen">
      <SEO
        title="Artists | Rain Boy Fine Art Gallery Tulsa"
        description="Browse contemporary artists represented by Rain Boy Fine Art in Tulsa, OK. Original paintings, sculpture, photography, and mixed media from emerging artists."
        path="/artists"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Artists", path: "/artists" }]}
        schemas={[{
          "@context": "https://schema.org", "@type": "CollectionPage", name: "Artists | Rain Boy Fine Art", url: "https://rainboyfineart.com/artists",
          mainEntity: { "@type": "ItemList", itemListElement: [
            { "@type": "ListItem", position: 1, item: { "@type": "Person", name: "Sarah Chen", url: "https://rainboyfineart.com/artists/sarah-chen" } },
            { "@type": "ListItem", position: 2, item: { "@type": "Person", name: "Sheldon Harvey", url: "https://rainboyfineart.com/artists/sheldon-harvey" } },
            { "@type": "ListItem", position: 3, item: { "@type": "Person", name: "Steve Latimer", url: "https://rainboyfineart.com/artists/steve-latimer" } },
            { "@type": "ListItem", position: 4, item: { "@type": "Person", name: "James Okafor", url: "https://rainboyfineart.com/artists/james-okafor" } },
            { "@type": "ListItem", position: 5, item: { "@type": "Person", name: "Marcus Rivera", url: "https://rainboyfineart.com/artists/marcus-rivera" } },
            { "@type": "ListItem", position: 6, item: { "@type": "Person", name: "Jeremy Salazar", url: "https://rainboyfineart.com/artists/jeremy-salazar" } },
            { "@type": "ListItem", position: 7, item: { "@type": "Person", name: "Elena Vasquez", url: "https://rainboyfineart.com/artists/elena-vasquez" } },
          ]}
        }]}
      />
      <section className="py-16 md:py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header with view toggle */}
          <div className="flex items-end justify-between mb-14">
            <FadeIn>
              <p className="overline mb-4">Gallery</p>
              <h1 className="section-heading font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0A1128]">
                Artists
              </h1>
            </FadeIn>
            <div className="flex items-center gap-1" data-testid="view-toggle">
              <button
                onClick={() => setView("grid")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors ${
                  view === "grid" ? "text-[#0A1128]" : "text-[#8A94A6] hover:text-[#0A1128]"
                }`}
                data-testid="view-grid-btn"
              >
                <LayoutGrid size={14} /> Grid
              </button>
              <button
                onClick={() => setView("list")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors ${
                  view === "list" ? "text-[#0A1128]" : "text-[#8A94A6] hover:text-[#0A1128]"
                }`}
                data-testid="view-list-btn"
              >
                <List size={14} /> List
              </button>
            </div>
          </div>

          {/* Reserve space while loading */}
          {!loaded && <div className="min-h-[60vh]" />}

          {/* Grid View */}
          {loaded && view === "grid" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {artists.map((artist) => (
                <FadeIn key={artist.id} ready={animateReady}>
                  <Link
                    to={`/artists/${artist.slug}`}
                    className="group block"
                    data-testid={`artist-directory-card-${artist.slug}`}
                  >
                    <div className="aspect-square overflow-hidden mb-4">
                      <img
                        src={getArtistImage(artist)}
                        alt={`Portrait of ${artist.name}, artist at Rain Boy Fine Art`}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>
                    <div className="transition-transform duration-500 ease-out group-hover:-translate-y-1">
                      <h2 className="font-heading text-lg text-[#0A1128] mb-1 relative inline-block">
                        {artist.name}
                        <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-[#CBA365] transition-all duration-500 ease-out group-hover:w-full" />
                      </h2>
                      <p className="text-sm text-[#8A94A6]">{artist.medium}</p>
                    </div>
                  </Link>
                </FadeIn>
              ))}
            </div>
          )}

          {/* List View */}
          {loaded && view === "list" && (
            <div className="divide-y divide-[#0A1128]/10">
              {artists.map((artist) => (
                <FadeIn key={artist.id} ready={animateReady}>
                  <Link
                    to={`/artists/${artist.slug}`}
                    className="group flex items-center justify-between py-6 pl-0 hover:pl-2 hover:bg-[#F8F9FA] transition-all duration-300 ease-in-out"
                    data-testid={`artist-list-item-${artist.slug}`}
                  >
                    <div className="flex items-center gap-6">
                      <div className="w-20 h-20 overflow-hidden shrink-0">
                        <img
                          src={getArtistImage(artist)}
                          alt={`Portrait of ${artist.name}, artist at Rain Boy Fine Art`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h2 className="font-heading text-xl text-[#0A1128] group-hover:text-[#CBA365] transition-colors duration-300">
                          {artist.name}
                        </h2>
                        <p className="text-sm text-[#8A94A6] mt-1">{artist.medium}</p>
                      </div>
                    </div>
                    <span className="text-xs uppercase tracking-[0.2em] text-[#8A94A6] group-hover:text-[#0A1128] transition-colors hidden sm:block">
                      View Work
                    </span>
                  </Link>
                </FadeIn>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
